import { useState, useEffect, useMemo, useCallback } from "react";
import { fetchCompanyChildAction } from "../../actions/fetchCompanyChildAction";

// ── Deterministic color palette ───────────────────────────────────────────────
// Assigned by parent index so the same parent always gets the same color.
const PALETTE = [
  { color: "#3B82F6", bg: "#EFF6FF" }, // blue
  { color: "#8B5CF6", bg: "#F5F3FF" }, // purple
  { color: "#22C55E", bg: "#F0FDF4" }, // green
  { color: "#F97316", bg: "#FFF7ED" }, // orange
  { color: "#EC4899", bg: "#FDF2F8" }, // pink
  { color: "#06B6D4", bg: "#ECFEFF" }, // cyan
  { color: "#A855F7", bg: "#FAF5FF" }, // violet
  { color: "#EAB308", bg: "#FEFCE8" }, // yellow
  { color: "#EF4444", bg: "#FEF2F2" }, // red
  { color: "#10B981", bg: "#ECFDF5" }, // emerald
  { color: "#F59E0B", bg: "#FFFBEB" }, // amber
  { color: "#6366F1", bg: "#EEF2FF" }, // indigo
];

const getColor = (idx) => PALETTE[idx % PALETTE.length];

// ── Abbreviation for the avatar (up to 2 chars) ───────────────────────────────
const abbrev = (name = "") => {
  const words = name.trim().split(/\s+/);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
};

// ── Map raw API records → PARENT_COMPANIES + DISTRIBUTORS shape ───────────────
// API shape:
//   [{ id, parentName, children: [{ id, parentName, entityId, isActive }] }]
//
// Special case: record where parentName === "" (id=0) → "No Parent" bucket.
//   Its children are distributors with no parent company.
// All other records: record.parentName is the company name,
//   child.parentName is the distributor's own display name.
const mapApiToView = (records = []) => {
  const parents = [];
  const distributors = [];

  const noParentRecord = records.find(
    (r) => !r.parentName || r.parentName.trim() === "",
  );
  const parentRecords = records.filter(
    (r) => r.parentName && r.parentName.trim() !== "",
  );

  // Build parent companies
  parentRecords.forEach((record, idx) => {
    const parentId = String(record.id);
    const parentName = record.parentName.trim();
    const children = record.children || [];
    const { color, bg } = getColor(idx);

    parents.push({
      id: parentId,
      code: abbrev(parentName),
      name: parentName,
      color,
      bg,
      distributorsCount: children.length,
      hasParent: false,
    });

    children.forEach((child) => {
      const distId = String(child.id);
      distributors.push({
        id: distId,
        code: child.entityId || abbrev(child.parentName || parentName),
        name: child.parentName?.trim() || child.entityId || distId,
        parentId,
        status: child.isActive !== false ? "Active" : "Inactive",
        isActive: child.isActive !== false,
        entityId: child.entityId || "",
      });
    });
  });

  // No-parent distributors (parentId = null → "no-parent" bucket)
  if (noParentRecord) {
    (noParentRecord.children || []).forEach((child) => {
      const distId = String(child.id);
      distributors.push({
        id: distId,
        code: child.entityId || abbrev(child.parentName || "NP"),
        name: child.parentName?.trim() || child.entityId || distId,
        parentId: null,
        status: child.isActive !== false ? "Active" : "Inactive",
        isActive: child.isActive !== false,
        entityId: child.entityId || "",
      });
    });
  }

  return { parents, distributors };
};

// ── Hook ──────────────────────────────────────────────────────────────────────
export function useCompanyDistributorData() {
  const [rawParents, setRawParents] = useState([]);
  const [rawDistributors, setRawDistributors] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [selectedParentId, setSelectedParentId] = useState(null);
  const [sortParents, setSortParents] = useState("asc");
  const [expandedGroups, setExpandedGroups] = useState({});

  // ── Fetch from API ──────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setIsLoading(true);
      const result = await fetchCompanyChildAction();
      if (!cancelled && result.success) {
        const { parents, distributors } = mapApiToView(result.data);
        setRawParents(parents);
        setRawDistributors(distributors);
        // Auto-select the first parent that has children
        const firstWithChildren = parents.find((p) => p.distributorsCount > 0);
        if (firstWithChildren) setSelectedParentId(firstWithChildren.id);
      }
      if (!cancelled) setIsLoading(false);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  // ── PARENT_COMPANIES equivalent (needed by index.js for color lookup) ───────
  // We expose rawParents as PARENT_COMPANIES so index.js can look up colors.
  // index.js already imports PARENT_COMPANIES from this file — we now export it
  // as a reactive value via the hook instead.

  // ── Filter + sort parents ───────────────────────────────────────────────────
  const filteredParents = useMemo(() => {
    let list = [...rawParents];
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (p) =>
          p.name.toLowerCase().includes(q) || p.code.toLowerCase().includes(q),
      );
    }
    list.sort((a, b) => {
      const cmp = a.name.localeCompare(b.name);
      return sortParents === "asc" ? cmp : -cmp;
    });
    return list;
  }, [rawParents, search, sortParents]);

  // ── Group distributors by parentId ─────────────────────────────────────────
  const distributorsByParent = useMemo(() => {
    const map = {};
    rawDistributors.forEach((d) => {
      const key = d.parentId || "no-parent";
      if (!map[key]) map[key] = [];
      map[key].push(d);
    });
    return map;
  }, [rawDistributors]);

  // ── Visible distributor list (right panel) ──────────────────────────────────
  // Shows all groups in order; collapses any group > 3 unless expanded.
  const MAX_COLLAPSED = 3;
  const visibleDistributors = useMemo(() => {
    const result = [];

    // Walk parents in current sorted order
    filteredParents.forEach((parent) => {
      const dists = distributorsByParent[parent.id] || [];
      if (dists.length === 0) return;

      const isExpanded = !!expandedGroups[parent.id];

      if (!isExpanded && dists.length > MAX_COLLAPSED) {
        result.push(...dists.slice(0, MAX_COLLAPSED));
        result.push({
          id: `more-${parent.id}`,
          type: "more",
          parentId: parent.id,
          count: dists.length - MAX_COLLAPSED,
          total: dists.length,
        });
      } else {
        result.push(...dists);
      }
    });

    // No Parent section
    const noParent = distributorsByParent["no-parent"] || [];
    if (noParent.length > 0) {
      const isExpanded = !!expandedGroups["no-parent"];
      result.push({
        id: "no-parent-header",
        type: "no-parent-header",
        count: noParent.length,
      });
      if (isExpanded) {
        result.push(...noParent);
      } else {
        result.push(...noParent.slice(0, MAX_COLLAPSED));
        if (noParent.length > MAX_COLLAPSED) {
          result.push({
            id: "more-no-parent",
            type: "more-no-parent",
            count: noParent.length - MAX_COLLAPSED,
          });
        }
      }
    }

    // Apply search filter
    if (search.trim()) {
      const q = search.toLowerCase();
      return result.filter((item) => {
        if (item.type) return true;
        return (
          item.name?.toLowerCase().includes(q) ||
          item.code?.toLowerCase().includes(q)
        );
      });
    }

    return result;
  }, [filteredParents, distributorsByParent, expandedGroups, search]);

  const totalParents = rawParents.length;
  const totalDistributors = rawDistributors.length;
  const noParentCount = (distributorsByParent["no-parent"] || []).length;

  const toggleExpand = useCallback((key) => {
    setExpandedGroups((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const toggleSortParents = () =>
    setSortParents((s) => (s === "asc" ? "desc" : "asc"));

  return {
    // data
    parents: filteredParents,
    distributors: visibleDistributors,
    allDistributors: rawDistributors,
    distributorsByParent,
    totalParents,
    totalDistributors,
    noParentCount,
    isLoading,
    // state
    search,
    setSearch,
    selectedParentId,
    setSelectedParentId,
    sortParents,
    expandedGroups,
    toggleExpand,
    toggleSortParents,
  };
}

// ── PARENT_COMPANIES is no longer a static export ─────────────────────────────
// index.js imports PARENT_COMPANIES for color lookup in calculateConnections.
// We re-export an empty array here so the import doesn't break at module load.
// The hook populates rawParents dynamically; index.js should use the `parents`
// returned by the hook for color lookups instead. The fix is applied in index.js
// by using a local lookup from the `parents` array.
export const PARENT_COMPANIES = [];
